import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { isGroupAdmin, isSuperAdmin } from '../../utils/roles';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const BreadcrumbTrail = () => {
  const location = useLocation();
  const { user, userProfile } = useAuth();

  const routeLabels: Record<string, string> = {
    'admin-dashboard': 'Admin Dashboard',
    'member-dashboard': 'Member Dashboard',
    'group-management': 'Group Management',
    'contribution-recording': 'Contribution Recording',
    'reports': 'Reports',
    'member-profile': 'Member Profile',
    'subscription-plan-management': 'Subscription Plan Management',
    'subscription-management': 'Subscription Management',
    'payment-processing': 'Payment Processing',
    'payment-status': 'Payment Status',
    'payment-instructions': 'Payment Instructions',
    'platform-analytics': 'Platform Analytics',
    'advanced-analytics': 'Advanced Analytics',
    'super-admin-dashboard': 'Super Admin Dashboard',
    'account-settings': 'Account Settings',
    'loan-request': 'Loan Requests',
    'notifications': 'Notifications',
    'invoices': 'Invoices',
  };

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    let currentPath = '';
    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`;
      const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      breadcrumbs.push({
        label,
        path: currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  const role = userProfile?.role;
  const homePath =
    user == null
      ? '/login'
      : isSuperAdmin(role)
        ? '/super-admin-dashboard'
        : isGroupAdmin(role)
          ? '/admin-dashboard'
          : '/member-dashboard';

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
      <Link
        to={homePath}
        className="text-muted-foreground hover:text-foreground transition-interactive flex items-center gap-1"
      >
        <Icon name="Home" size={16} />
        <span className="hidden sm:inline">Home</span>
      </Link>

      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return (
          <div key={crumb.path} className="flex items-center gap-2">
            <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            {isLast ? (
              <span className="text-foreground font-medium truncate max-w-[200px] sm:max-w-none">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className="text-muted-foreground hover:text-foreground transition-interactive truncate max-w-[150px] sm:max-w-none"
              >
                {crumb.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default BreadcrumbTrail;